import { useState, useEffect } from "react";
import { useParams, useNavigate } from "react-router-dom";
import Navbar from "../home/components/Navbar";
import Footer from "../home/components/Footer";
import QuoteModal from "../home/components/QuoteModal";
import ServiceHero from "./components/ServiceHero";
import HowItWorks from "./components/HowItWorks";
import PricingSection from "./components/PricingSection";
import ServiceFAQ from "./components/ServiceFAQ";
import NotFound from "../NotFound";
import { servicePages } from "../../mocks/servicePages";
import { productsApi, cmsApi, type WCProduct, type ServicePageCMS } from "../../lib/api";
import type { ServicePageData, ServiceTier } from "../../mocks/servicePages";

export default function ServicePage() {
  const { slug } = useParams<{ slug: string }>();
  const navigate = useNavigate();
  const [modalOpen, setModalOpen] = useState(false);
  const [wcProduct, setWcProduct] = useState<WCProduct | null>(null);
  const [cmsData, setCmsData] = useState<ServicePageCMS | null>(null);

  const mockService = servicePages.find((s) => s.slug === slug);

  useEffect(() => {
    window.scrollTo(0, 0);
  }, [slug]);

  // Fetch live WC product to get real pricing for tiers
  useEffect(() => {
    if (!slug) return;
    productsApi.getBySlug(slug).then(setWcProduct).catch(() => {});
  }, [slug]);

  // Fetch CMS overrides for this service page
  useEffect(() => {
    if (!slug) return;
    cmsApi.getServicePage(slug).then((data) => {
      if (data) setCmsData(data);
    });
  }, [slug]);

  if (!mockService) return <NotFound />;

  // Deep-merge CMS data over mock defaults — CMS wins for any non-empty value
  const service: ServicePageData = {
    ...mockService,
    ...(cmsData?.title        ? { title:        cmsData.title        } : {}),
    ...(cmsData?.badge        ? { badge:         cmsData.badge        } : {}),
    ...(cmsData?.badgeType    ? { badgeType:     cmsData.badgeType    } : {}),
    ...(cmsData?.headline     ? { headline:      cmsData.headline     } : {}),
    ...(cmsData?.subheadline  ? { subheadline:   cmsData.subheadline  } : {}),
    ...(cmsData?.description  ? { description:   cmsData.description  } : {}),
    ...(cmsData?.priceFrom    ? { priceFrom:     cmsData.priceFrom    } : {}),
    ...(cmsData?.heroImage    ? { heroImage:     cmsData.heroImage    } : {}),
    ...(cmsData?.productImage ? { productImage:  cmsData.productImage } : {}),
    ...(cmsData?.steps?.length  ? { steps:   cmsData.steps   } : {}),
    ...(cmsData?.tiers?.length  ? { tiers:   cmsData.tiers   } : {}),
    ...(cmsData?.accepts?.length? { accepts: cmsData.accepts } : {}),
    ...(cmsData?.faqs?.length   ? { faqs:    cmsData.faqs    } : {}),
  };

  // Merge WC variation prices onto the merged tiers
  const liveTiers: ServiceTier[] = service.tiers.map((tier) => {
    if (!wcProduct?.variations?.length) return tier;
    const match = wcProduct.variations.find(
      (v) =>
        v.label.toLowerCase().includes(tier.label.toLowerCase().split(" ")[0]) ||
        tier.label.toLowerCase().includes(v.label.toLowerCase().split(" ")[0])
    );
    if (!match) return tier;
    return {
      ...tier,
      price: `£${match.price.toFixed(2)}`,
    };
  });

  const handleBookNow = () => {
    navigate(`/book?service=${service.slug}`);
  };

  return (
    <main className="font-sans bg-white">
      <Navbar onBookNow={() => setModalOpen(true)} />
      <ServiceHero service={service} onBookNow={handleBookNow} />
      <HowItWorks steps={service.steps} />
      <PricingSection
        tiers={liveTiers}
        accepts={service.accepts}
        service={service}
        onBookNow={handleBookNow}
      />
      <ServiceFAQ faqs={service.faqs} onBookNow={handleBookNow} />
      <Footer />
      <QuoteModal isOpen={modalOpen} onClose={() => setModalOpen(false)} />

      {/* Mobile sticky CTA */}
      <div className="fixed bottom-6 right-6 z-40 md:hidden">
        <button
          onClick={handleBookNow}
          className="bg-cl-dark text-white font-bold px-6 py-3.5 rounded-full cursor-pointer whitespace-nowrap flex items-center gap-2 cartoon-btn"
        >
          <i className="ri-price-tag-3-line"></i>
          Book Now
        </button>
      </div>
    </main>
  );
}
